import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "@/data/site";

const DETAILS = [
  { src: IMAGES.cabinWide, alt: "Full cabin from the rear — swivel seat, console, galley and cooler", caption: "D.01 / CABIN WIDE", span: "md:col-span-6" },
  { src: IMAGES.bedMode, alt: "Bench seat, galley cabinet and cooler — the living room", caption: "D.02 / LIVING ROOM", span: "md:col-span-6" },
  { src: IMAGES.galleyOpen, alt: "Galley cabinet open with Yeti cooler aboard", caption: "D.03 / GALLEY, OPEN", span: "md:col-span-6" },
  { src: IMAGES.swivel, alt: "Passenger swivel seat and dashboard", caption: "D.04 / SWIVEL", span: "md:col-span-6" },
  { src: IMAGES.console, alt: "Custom center console with cup holders and cargo net", caption: "D.05 / CONSOLE", span: "md:col-span-6" },
  { src: IMAGES.command, alt: "Pioneer touchscreen, Big Shot shifter and inclinometer", caption: "D.06 / COMMAND", span: "md:col-span-6" },
  { src: IMAGES.rearBerth, alt: "Rear berth platform looking forward through the cabin", caption: "D.07 / REAR BERTH", span: "md:col-span-6" },
  { src: IMAGES.berthExt, alt: "Berth fully extended into a sleeping platform", caption: "D.08 / BERTH, EXTENDED", span: "md:col-span-6" },
  { src: IMAGES.doorPanel, alt: "ABS door panel with Kicker speaker and hardware", caption: "D.09 / DOOR PANEL", span: "md:col-span-6" },
  { src: IMAGES.subwoofer, alt: "Kicker subwoofer behind the front seat", caption: "D.10 / LOW END", span: "md:col-span-6" },
];

export default function Details() {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open === null) return;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o + 1) % DETAILS.length);
      if (e.key === "ArrowLeft") setOpen((o) => (o - 1 + DETAILS.length) % DETAILS.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section id="details" className="py-24 md:py-40 border-t border-border bg-muted" data-testid="details-section">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14 md:mb-20"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4">Up close</p>
            <h2 className="font-headings uppercase text-5xl md:text-7xl leading-[0.85]">
              The<br /><span className="text-primary">Details</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground max-w-xs" data-testid="details-note">
            Intimate interior shots
          </p>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-4 md:gap-5 items-start">
          {DETAILS.map((d, i) => (
            <motion.figure
              key={d.caption}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group col-span-1 cursor-pointer ${d.span}`}
              onClick={() => setOpen(i)}
              role="button"
              aria-label={`Open ${d.caption} full size`}
              data-testid={`detail-item-${i}`}
            >
              <div className="overflow-hidden border border-border bg-secondary" style={{ clipPath: i % 2 === 0 ? "polygon(0 0, 100% 0, 100% 100%, 1.5% 100%)" : "polygon(1.5% 0, 100% 0, 100% 100%, 0 100%)" }}>
                <img
                  src={d.src}
                  alt={d.alt}
                  loading="lazy"
                  className="w-full h-auto img-warm"
                />
              </div>
              <figcaption className="mt-2 font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-muted-foreground">
                {d.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] bg-foreground/90 backdrop-blur-md flex flex-col items-center justify-center p-4 md:p-12"
            onClick={() => setOpen(null)}
            data-testid="details-lightbox-overlay"
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute top-5 right-5 w-11 h-11 border border-background/40 text-background flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
              aria-label="Close"
              data-testid="details-lightbox-close-button"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setOpen((o) => (o - 1 + DETAILS.length) % DETAILS.length); }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 border border-background/40 text-background flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
              aria-label="Previous photo"
              data-testid="details-lightbox-prev-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setOpen((o) => (o + 1) % DETAILS.length); }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 border border-background/40 text-background flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
              aria-label="Next photo"
              data-testid="details-lightbox-next-button"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <motion.img
              key={DETAILS[open].src}
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              src={DETAILS[open].src}
              alt={DETAILS[open].alt}
              className="max-h-[82vh] max-w-full w-auto border-[6px] border-card shadow-2xl"
              data-testid="details-lightbox-image"
            />
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-background/70" data-testid="details-lightbox-caption">
              {DETAILS[open].caption}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
